import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import SEOHead from '../components/SEOHead';

function GalleryCard({ img, altText }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [hovered, setHovered] = useState(false);

  const showImage = img.src && !imgFailed;
  const showFallback = !img.src || imgFailed;

  return (
    <div
      style={{
        background: '#111827',
        aspectRatio: '4/3',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: showImage ? 'pointer' : 'default',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Real image */}
      {showImage && (
        <>
          <img
            src={img.src}
            alt={altText || img.title}
            width={560}
            height={420}
            loading="lazy"
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', display: imgLoaded ? 'block' : 'none',
              transition: 'transform 0.5s ease, filter 0.5s ease',
              transform: hovered ? 'scale(1.05)' : 'scale(1)',
              filter: hovered ? 'brightness(0.8)' : 'brightness(0.65)',
            }}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
          />
          {/* Caption overlay */}
          {imgLoaded && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2rem 1.5rem 1.2rem',
              background: 'linear-gradient(to top, rgba(12,18,32,0.95), transparent)',
              zIndex: 1,
            }}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.3rem', color: '#D42B2B', marginBottom: '0.3rem' }}>
                {img.title}
              </div>
              <div style={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                {img.desc}
              </div>
            </div>
          )}
        </>
      )}

      {/* Fallback: emoji + text (shown if no src or image failed to load) */}
      {(showFallback || !imgLoaded) && (
        <div style={{ textAlign: 'center', padding: '3rem', display: imgLoaded ? 'none' : 'block' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{'\uD83D\uDD27'}</div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.3rem', color: '#D42B2B', marginBottom: '0.5rem' }}>
            {img.title}
          </div>
          <div style={{ color: '#94A3B8', fontSize: '0.85rem' }}>
            {img.desc}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Gallery() {
  const { t } = useLanguage();
  const { mc } = useMarketingCopy();

  const images = (t.gallery.images || []).map((img, i) => ({ id: i + 1, ...img }));

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', paddingTop: 'var(--cjpa-nav-offset)' }}>
      <SEOHead page="gallery" />
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 2rem)' }}>

        <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '1rem' }}>
            {mc('gallery', 'subtitle', t.gallery.subtitle)}
          </div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(3rem, 6vw, 5rem)', color: '#E8ECF0', fontWeight: 300, marginBottom: '1.5rem' }}>
            {t.gallery.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.gallery.titleHighlight}</span>
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '0.95rem', maxWidth: '600px', margin: '0 auto', lineHeight: 1.8 }}>
            {mc('gallery', 'description', t.gallery.description)}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '1px', background: 'rgba(148,163,184,0.08)' }}>
          {images.map(img => (
            <GalleryCard
              key={img.id}
              img={img}
              altText={t.images?.[img.altKey] || ''}
            />
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '4rem', padding: '2rem', background: 'rgba(148,163,184,0.03)', border: '1px solid rgba(148,163,184,0.1)' }}>
          <p style={{ color: '#64748B', fontSize: '0.9rem', fontStyle: 'italic' }}>
            {mc('gallery', 'comingSoon', t.gallery.comingSoon)}
          </p>
        </div>

      </div>
    </div>
  );
}
